import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Clock, Monitor, MapPin, ArrowRight, Loader2, Calendar } from 'lucide-react';
import PageHero from '../../components/public/PageHero';
import Reveal from '../../components/public/Reveal';
import api from '../../lib/api';

const MODALITY_LABEL = { presencial: 'Presencial', zoom: 'Vía Zoom', hibrido: 'Híbrido' };

function formatDate(dt) {
  if (!dt) return 'Fechas a definir';
  try {
    const d = new Date(dt);
    return d.toLocaleDateString('es-BO', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return ''; }
}

export default function Capacitacion() {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/trainings');
        setTrainings(data || []);
      } catch {
        setTrainings([]);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div>
      <PageHero
        eyebrow="Capacitación"
        title="Campus CNIB: formación que certifica tu talento"
        description="Diplomados, cursos y certificaciones internacionales con aval de la Cámara para socios y profesionales del sector."
      />

      <section className="py-20 md:py-24" data-testid="capacitacion-page">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-10 w-10 animate-spin text-cnib-blue" /></div>
          ) : trainings.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-14 text-center">
              <GraduationCap className="mx-auto h-10 w-10 text-slate-400" />
              <p className="mt-4 font-heading text-lg font-bold text-slate-700">Pronto publicaremos nuestras próximas capacitaciones.</p>
              <p className="mt-1 text-sm text-slate-500">Mantente atento a las certificaciones internacionales y programas CNIB.</p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="trainings-grid">
              {trainings.map((t, i) => {
                const primary = t.primary_color || '#F5831F';
                const minPrice = (t.prices || []).reduce((min, p) => p.amount_usd > 0 && (min == null || p.amount_usd < min) ? p.amount_usd : min, null);
                return (
                  <Reveal key={t.training_id} delay={i * 0.06}>
                    <Link
                      to={`/capacitacion/${t.slug}`}
                      className="group block h-full overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                      data-testid={`training-card-${t.slug}`}
                    >
                      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                        {t.cover_image ? (
                          <img src={t.cover_image} alt={t.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center" style={{ background: `linear-gradient(135deg, ${primary}, ${t.accent_color || '#0F4C81'})` }}>
                            <GraduationCap className="h-16 w-16 text-white/50" />
                          </div>
                        )}
                        {t.eyebrow && (
                          <span className="absolute left-3 top-3 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white" style={{ background: primary }}>
                            {t.eyebrow}
                          </span>
                        )}
                      </div>
                      <div className="p-6">
                        <h3 className="font-heading text-xl font-extrabold text-slate-900 group-hover:text-cnib-blue">{t.title}</h3>
                        {t.subtitle && <p className="mt-1 text-sm text-slate-500">{t.subtitle}</p>}
                        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" style={{ color: primary }} /> {formatDate(t.starts_at)}</span>
                          {t.duration && <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" style={{ color: primary }} /> {t.duration}</span>}
                          <span className="flex items-center gap-1.5"><Monitor className="h-3.5 w-3.5" style={{ color: primary }} /> {MODALITY_LABEL[t.modality] || t.modality}</span>
                          {t.city && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" style={{ color: primary }} /> {t.city}</span>}
                        </div>
                        <div className="mt-5 flex items-end justify-between">
                          <div>
                            {minPrice != null && (
                              <>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Desde</p>
                                <p className="font-heading text-2xl font-extrabold text-slate-900">US$ {minPrice}</p>
                              </>
                            )}
                          </div>
                          <span className="inline-flex items-center gap-1 text-sm font-bold" style={{ color: primary }}>
                            Ver detalle <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
